// Team Chat's real-time connection — a module SINGLETON so React StrictMode's
// double effects, remounts, and route changes can never open a second socket
// (the viewerSettingsService / multiScreenService lifecycle pattern). The
// TeamChatProvider drives ensureStarted()/stop() from auth + context state.
//
// It talks to the real-time service (backend/Realtime, whose README is the
// contract): a short-lived single-use TICKET minted with the user's access token
// opens a WebSocket-only SignalR connection to `<realtime base>/realtime/hub`.
// Two kinds of traffic arrive:
// - `event`: sequenced, buffered notifications the chat service (and WebApi)
//   publish for this user. Kept gap-free here (`lastSeq`, hold-and-resume) and
//   re-emitted under the handler names the UI already uses.
// - `room`: unsequenced live-session relays (viewport state, pointer) and room
//   membership changes. A room id is a collab session id; joining one takes a
//   single-use grant from the chat service, renewed before it expires.
//
// Writes (status, typing, messages) are HTTP to the chat service; the socket
// only pushes. Bundle: @microsoft/signalr is imported dynamically on the first
// start, so users without a chat-enabled group (the default) never download it.
import { ApiError, ensureFreshAccessToken, getAccessToken, resolveRealtimeBase, } from '@/lib/api/client';
import { getCollabSession, sendTeamTyping, setTeamStatus } from '@/lib/api/team';
import { makeDiagThrottle, recordTeamHubDiag, teamHubClock, } from '@/lib/team/teamHubDiagnostics';
/** Published event names (backend/Chat README, "Events published") → the UI's handler names. */
const EVENT_HANDLERS = {
    'chat.message.received': 'messageReceived',
    'chat.message.deleted': 'messageDeleted',
    'chat.reaction.changed': 'reactionChanged',
    'chat.conversation.created': 'conversationCreated',
    'chat.conversation.archived': 'conversationArchived',
    'chat.typing': 'typing',
    'chat.presence.changed': 'presenceChanged',
    'chat.collab.ended': 'collabEnded',
    // Published by WebApi (Services/ChatCaseCounts.cs), not the chat service.
    'chat.case.counts.changed': 'caseCountsChanged',
};
/** Wire names the presenter relays into its room; followers match them against the incoming
 *  `room` message name. These MUST be lowercase-dotted: the realtime service validates every
 *  relay name against `^[a-z0-9]+([._-][a-z0-9]+)*$` (see
 *  `backend/Realtime/src/Events/PublishEndpoint.cs` `NamePattern`), so a camelCase name like
 *  `collabState` is rejected with `invalid_name` and the relay is silently dropped — which is
 *  exactly how live viewer-session mirroring broke. Distinct from the client-internal event-bus
 *  names `collabState`/`collabPointer` (see `TeamHubEventMap` and the `emit` calls below), which
 *  never go to the server and stay camelCase. Exported so teamHub.test.ts can assert they satisfy
 *  the server's pattern. */
export const COLLAB_STATE = 'collab.state';
export const COLLAB_POINTER = 'collab.pointer';
const MEMBER_JOINED = 'room.member.joined';
const MEMBER_LEFT = 'room.member.left';
const ROOM_EXPIRED = 'room.expired';
const ROOM_REVOKED = 'room.revoked';
const MEMBERSHIP_MESSAGES = new Set([MEMBER_JOINED, MEMBER_LEFT, ROOM_EXPIRED, ROOM_REVOKED]);
/** How long a sequence gap may stay open before `resume` is asked for the missing events. */
const GAP_WAIT_MS = 1_000;
/** Renew a room grant at this fraction of its remaining lifetime. */
const GRANT_REFRESH_FRACTION = 0.8;
/** A renewal that failed transiently is tried again after this long. */
const GRANT_RETRY_MS = 10_000;
/** Never renew sooner than this after a join, so a skewed client clock cannot spin renewals. */
const MIN_GRANT_REFRESH_MS = 30_000;
/** The chat service takes one status change a second (429 for the rest): a throttled choice is
 * sent again once that window has passed. */
const STATUS_RETRY_MS = 1_100;
/** How long the liveness Ping may take before a "connected" socket is treated as dead. Generous on
 * purpose: the target is a socket that answers NEVER (a server-reaped half-open connection the
 * browser never saw close), not merely a slow one, so a tolerant deadline avoids false reconnects
 * on a brief hiccup while still catching the deaf-forever case. */
const LIVENESS_TIMEOUT_MS = 7_000;
/** The code a server-side HubException carries (`"... HubException: grant_used"`), else null. */
function hubErrorCode(err) {
    const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
    return /HubException: ([a-z_]+)/.exec(message)?.[1] ?? null;
}
/**
 * A fresh single-use connection ticket. Refresh-aware: the access token is renewed first when
 * it is about to expire. A plain fetch rather than apiFetch on purpose: a refused ticket must
 * fail this connection attempt (SignalR and the provider retry it), never sign the user out
 * the way apiFetch's 401 handling would.
 */
async function mintTicket() {
    await ensureFreshAccessToken();
    const token = getAccessToken();
    const response = await fetch(`${resolveRealtimeBase()}/realtime/ticket`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok)
        throw new Error(`Real-time ticket refused (${response.status})`);
    const body = (await response.json());
    return body.ticket;
}
/** The session with a fresh grant; null when the caller may not be in it (403/404). */
async function fetchSession(sessionId) {
    try {
        return await getCollabSession(sessionId);
    }
    catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404))
            return null;
        throw err;
    }
}
function distinctUsers(members) {
    return new Set(members.values());
}
class TeamHubService {
    connection = null;
    startPromise = null;
    listeners = new Map();
    state = 'idle';
    /** Bumped by stop(): a start still awaiting the SignalR chunk must not build
     *  a connection the caller has since asked to stop. */
    generation = 0;
    /** Bumped whenever the connection's server-side identity changes (connected, reconnecting,
     *  closed); a room joined in an older epoch must be joined again. */
    epoch = 0;
    overConnectionLimit = false;
    // Sequencing: lastSeq is the highest seq applied with nothing missing below it.
    lastSeq = null;
    held = new Map();
    /** Diagnostics only: the monotonic clock reading when an inbound event was last processed, so a
     *  "connected but deaf" tab (nothing arriving for minutes) shows up in the heartbeat. */
    lastAppliedAt = null;
    gapTimer = null;
    resuming = false;
    resumeAgain = false;
    /** A liveness probe is in flight: a periodic check and a visibility check must not race. */
    verifying = false;
    rooms = new Map();
    // Per-relay-name diagnostic recorders for the collab path, so the ~25 Hz pointer and ~10 Hz state
    // relays never flood the 200-entry ring. Relay outcomes and dropped (no-room) receives are
    // edge-only (recorded when the outcome changes); delivered receives are throttled to ~1/s with a
    // coalesced count. Keyed per name so an alternating state/pointer stream does not log every call.
    relayDiags = new Map();
    recvDiags = new Map();
    recvDropDiags = new Map();
    diagFor(map, name, intervalMs) {
        let throttle = map.get(name);
        if (!throttle) {
            throttle = makeDiagThrottle(intervalMs);
            map.set(name, throttle);
        }
        return throttle;
    }
    /** The newest status asked for, and the one retry queued behind a 429 (which sends whatever
     * is newest when it fires). */
    wantedStatus = null;
    statusRetry = null;
    getState() {
        return this.state;
    }
    /** True after the service refused this connection because the user already holds the
     *  maximum number of connections; cleared by the next successful connect or stop(). The
     *  provider must not restart in a loop while it is set. */
    isOverConnectionLimit() {
        return this.overConnectionLimit;
    }
    on(event, callback) {
        let set = this.listeners.get(event);
        if (!set) {
            set = new Set();
            this.listeners.set(event, set);
        }
        set.add(callback);
        return () => set.delete(callback);
    }
    emit(event, payload) {
        const set = this.listeners.get(event);
        if (!set)
            return;
        for (const cb of [...set]) {
            try {
                ;
                cb(payload);
            }
            catch (err) {
                // A broken listener must not break delivery to the others.
                console.error('teamHub listener failed', err);
            }
        }
    }
    setState(state, reason) {
        if (this.state === state)
            return;
        const from = this.state;
        this.state = state;
        recordTeamHubDiag('state', {
            from,
            to: state,
            reason,
            epoch: this.epoch,
            generation: this.generation,
            overConnectionLimit: this.overConnectionLimit,
        });
        this.emit('connectionStateChanged', state);
    }
    /** Sets the "too many connections" flag and records the transition (set/cleared). */
    setOverConnectionLimit(value, reason) {
        if (this.overConnectionLimit === value)
            return;
        this.overConnectionLimit = value;
        recordTeamHubDiag('overConnectionLimit', { value, reason });
    }
    /** A snapshot of the receive-path state for the diagnostic heartbeat (see teamHubDiagnostics).
     *  ids, counts and state names only. */
    diagnostics() {
        return {
            state: this.state,
            lastSeq: this.lastSeq,
            heldCount: this.held.size,
            epoch: this.epoch,
            generation: this.generation,
            overConnectionLimit: this.overConnectionLimit,
            rooms: this.rooms.size,
            msSinceLastApplied: this.lastAppliedAt === null ? null : Math.round(teamHubClock() - this.lastAppliedAt),
        };
    }
    /** Idempotent: concurrent/repeat callers share one start. */
    async ensureStarted() {
        if (this.isConnected())
            return;
        if (this.startPromise)
            return this.startPromise;
        const promise = this.startInternal().finally(() => {
            if (this.startPromise === promise)
                this.startPromise = null;
        });
        this.startPromise = promise;
        return promise;
    }
    /** HubConnectionState is a string enum; compared by value so the module
     *  needs no runtime import of SignalR. */
    isConnected() {
        return this.connection?.state === 'Connected';
    }
    async startInternal() {
        // Claimed synchronously (ensureStarted calls this without awaiting first),
        // so a stop() issued at any point after ensureStarted() cancels this start.
        const generation = ++this.generation;
        await this.teardown();
        if (generation !== this.generation)
            return;
        this.setState('connecting', 'start');
        let signalr;
        try {
            signalr = await import(/* webpackChunkName: "signalr" */ '@microsoft/signalr');
        }
        catch (err) {
            if (generation === this.generation)
                this.setState('idle', 'signalr_import_failed');
            throw err;
        }
        if (generation !== this.generation)
            return;
        const connection = new signalr.HubConnectionBuilder()
            .withUrl(`${resolveRealtimeBase()}/realtime/hub`, {
            transport: signalr.HttpTransportType.WebSockets,
            skipNegotiation: true,
            // Called on every connect AND automatic reconnect: a ticket opens one connection only.
            accessTokenFactory: mintTicket,
        })
            // The service closes every connection after 60 minutes, so reconnects are routine.
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(signalr.LogLevel.Warning)
            .build();
        // Lifecycle callbacks of a connection that has since been replaced (or
        // stopped) must not overwrite the state of the current one.
        const current = () => this.connection === connection;
        connection.on('event', (envelope) => {
            if (current())
                this.acceptEvent(envelope);
        });
        connection.on('room', (message) => {
            if (current())
                this.onRoomMessage(message);
        });
        connection.onreconnecting((error) => {
            if (!current())
                return;
            recordTeamHubDiag('reconnecting', { code: hubErrorCode(error), epoch: this.epoch });
            this.loseConnection();
            this.setState('reconnecting', 'signalr_reconnecting');
        });
        connection.onreconnected(() => {
            if (!current())
                return;
            recordTeamHubDiag('reconnected', { epoch: this.epoch });
            this.onConnected();
        });
        connection.onclose((error) => {
            if (!current())
                return;
            const code = hubErrorCode(error);
            const tooMany = code === 'too_many_connections';
            // onclose fires only once SignalR's own automatic reconnect is exhausted (or never ran); the
            // provider's restart loop takes over from 'idle', and deliberately stops on a cap refusal.
            recordTeamHubDiag('close', { code, tooManyConnections: tooMany, epoch: this.epoch });
            if (tooMany)
                this.setOverConnectionLimit(true, 'close:too_many_connections');
            this.connection = null;
            this.loseConnection();
            this.setState('idle', code ? `close:${code}` : 'close');
        });
        this.connection = connection;
        try {
            await connection.start();
        }
        catch (err) {
            if (current()) {
                const code = hubErrorCode(err);
                recordTeamHubDiag('startFailed', { code });
                if (code === 'too_many_connections')
                    this.setOverConnectionLimit(true, 'start:too_many_connections');
                this.connection = null;
                this.setState('idle', code ? `start_failed:${code}` : 'start_failed');
            }
            throw err;
        }
        if (current() && this.isConnected())
            this.onConnected();
    }
    /** A connection (first or automatic re-connect) is up: re-join every room this client is in,
     *  announce the state, then ask for whatever was missed. */
    onConnected() {
        this.setOverConnectionLimit(false, 'connected');
        this.epoch += 1;
        recordTeamHubDiag('onConnected', {
            epoch: this.epoch,
            rooms: this.rooms.size,
            lastSeq: this.lastSeq,
        });
        for (const room of this.rooms.values()) {
            if (!room.joining)
                void this.startJoin(room.sessionId, null).catch(() => { });
        }
        this.setState('connected', 'connected');
        void this.resume();
    }
    /** The server-side connection is gone: rooms must be joined again, timers stop. */
    loseConnection() {
        this.epoch += 1;
        this.clearGapTimer();
        for (const room of this.rooms.values())
            this.clearRefresh(room);
    }
    async stop() {
        this.generation += 1;
        // A start in flight is now cancelled: the next ensureStarted() must begin
        // a fresh one rather than join it (StrictMode's start/stop/start).
        this.startPromise = null;
        // The next start may be another user's: forget the stream position and every room.
        this.lastSeq = null;
        this.held.clear();
        this.clearGapTimer();
        this.resumeAgain = false;
        for (const room of this.rooms.values())
            this.clearRefresh(room);
        this.rooms.clear();
        this.setOverConnectionLimit(false, 'stop');
        await this.teardown();
    }
    async teardown() {
        const connection = this.connection;
        this.connection = null;
        if (connection) {
            this.loseConnection();
            try {
                await connection.stop();
            }
            catch {
                // Already dead — that's what we wanted.
            }
        }
        this.setState('idle', 'teardown');
    }
    // --- Sequenced events -----------------------------------------------------
    acceptEvent(envelope) {
        if (this.lastSeq === null) {
            // No baseline yet (the first resume is in flight, or failed): hold until one answers. The
            // gap timer retries a resume that failed, so the held events are not stuck until the next
            // reconnect; it does nothing while a resume is in flight.
            recordTeamHubDiag('event:received', {
                seq: envelope.seq,
                name: envelope.name,
                lastSeq: null,
                outcome: 'held-nobaseline',
            });
            this.held.set(envelope.seq, envelope);
            this.armGapTimer();
            return;
        }
        if (envelope.seq <= this.lastSeq) {
            recordTeamHubDiag('event:received', {
                seq: envelope.seq,
                name: envelope.name,
                lastSeq: this.lastSeq,
                outcome: 'ignored-old',
            });
            return;
        }
        recordTeamHubDiag('event:received', {
            seq: envelope.seq,
            name: envelope.name,
            lastSeq: this.lastSeq,
            outcome: envelope.seq === this.lastSeq + 1 ? 'applied' : 'held-gap',
        });
        this.held.set(envelope.seq, envelope);
        this.drainHeld();
    }
    /** Applies held events that are now contiguous; drops the ones already covered. */
    drainHeld() {
        if (this.lastSeq === null)
            return;
        for (const seq of [...this.held.keys()]) {
            if (seq <= this.lastSeq)
                this.held.delete(seq);
        }
        let next = this.held.get(this.lastSeq + 1);
        while (next) {
            this.held.delete(next.seq);
            this.lastSeq = next.seq;
            this.applyEvent(next);
            next = this.held.get(this.lastSeq + 1);
        }
        if (this.held.size === 0) {
            if (this.gapTimer !== null)
                recordTeamHubDiag('gap:close', { lastSeq: this.lastSeq });
            this.clearGapTimer();
        }
        else
            this.armGapTimer();
    }
    applyEvent(envelope) {
        // Any inbound event the stream processes counts as receive-path activity for the heartbeat.
        this.lastAppliedAt = teamHubClock();
        const handler = EVENT_HANDLERS[envelope.name];
        if (!handler) {
            recordTeamHubDiag('event:unhandled', { seq: envelope.seq, name: envelope.name });
            console.debug(`teamHub ignored event ${envelope.name}`);
            return;
        }
        recordTeamHubDiag('event:applied', { seq: envelope.seq, name: envelope.name });
        if (handler === 'collabEnded') {
            const sessionId = envelope.payload.sessionId;
            this.dropRoom(sessionId);
        }
        this.emit(handler, envelope.payload);
    }
    armGapTimer() {
        if (this.gapTimer !== null || this.resuming)
            return;
        recordTeamHubDiag('gap:open', { heldCount: this.held.size, lastSeq: this.lastSeq });
        this.gapTimer = setTimeout(() => {
            this.gapTimer = null;
            if (this.held.size > 0) {
                recordTeamHubDiag('gap:timeout', { heldCount: this.held.size, lastSeq: this.lastSeq });
                void this.resume();
            }
        }, GAP_WAIT_MS);
    }
    clearGapTimer() {
        if (this.gapTimer !== null)
            clearTimeout(this.gapTimer);
        this.gapTimer = null;
    }
    /** `resume(lastSeq)`: the missed events in order, or `resync` when they are gone. */
    async resume() {
        const connection = this.connection;
        if (!connection || !this.isConnected()) {
            recordTeamHubDiag('resume:skip', { reason: 'not_connected', lastSeq: this.lastSeq });
            return;
        }
        if (this.resuming) {
            this.resumeAgain = true;
            recordTeamHubDiag('resume:queued', { lastSeq: this.lastSeq });
            return;
        }
        this.resuming = true;
        this.clearGapTimer();
        const lastSeqBefore = this.lastSeq;
        recordTeamHubDiag('resume:send', { lastSeq: lastSeqBefore });
        try {
            const result = await connection.invoke('resume', this.lastSeq);
            if (this.connection !== connection) {
                recordTeamHubDiag('resume:stale', { lastSeq: lastSeqBefore });
                return;
            }
            recordTeamHubDiag('resume:result', {
                resync: result.resync,
                currentSeq: result.currentSeq,
                events: result.events.length,
                lastSeqBefore,
            });
            if (result.resync || this.lastSeq === null) {
                // Never backwards: live events may have advanced past currentSeq during the await.
                this.lastSeq = Math.max(this.lastSeq ?? result.currentSeq, result.currentSeq);
                this.emit('resync', undefined);
            }
            else {
                for (const envelope of result.events) {
                    if (envelope.seq > this.lastSeq)
                        this.held.set(envelope.seq, envelope);
                }
                this.drainHeld();
                this.lastSeq = Math.max(this.lastSeq, result.currentSeq);
            }
            this.drainHeld();
        }
        catch (err) {
            // The next (re)connect resumes again; nothing is lost while lastSeq stays put.
            recordTeamHubDiag('resume:failed', {
                lastSeq: lastSeqBefore,
                error: err instanceof Error ? err.message : String(err),
            });
            console.warn('teamHub resume failed', err);
        }
        finally {
            this.resuming = false;
            if (this.resumeAgain) {
                this.resumeAgain = false;
                void this.resume();
            }
            else if (this.held.size > 0 && this.connection === connection) {
                this.armGapTimer();
            }
        }
    }
    // --- Rooms (live sessions) ------------------------------------------------
    onRoomMessage(message) {
        const room = this.rooms.get(message.roomId);
        if (!room) {
            // A relay arrived for a room this client is not (yet) in: the follower cannot apply it. A
            // real Problem-1 signature if it persists, so record it (edge-only, per name).
            this.diagFor(this.recvDropDiags, message.name, Infinity)(message.name, 'collab:recv', {
                roomId: message.roomId,
                name: message.name,
                outcome: 'no_room',
            });
            return;
        }
        if (message.name === COLLAB_STATE) {
            this.diagFor(this.recvDiags, COLLAB_STATE, 1_000)('recv', 'collab:recv', {
                sessionId: room.sessionId,
                name: COLLAB_STATE,
            });
            this.emit('collabState', {
                sessionId: room.sessionId,
                state: message.payload,
            });
            return;
        }
        if (message.name === COLLAB_POINTER) {
            this.diagFor(this.recvDiags, COLLAB_POINTER, 1_000)('recv', 'collab:recv', {
                sessionId: room.sessionId,
                name: COLLAB_POINTER,
            });
            this.emit('collabPointer', {
                sessionId: room.sessionId,
                pointer: message.payload,
            });
            return;
        }
        if (!MEMBERSHIP_MESSAGES.has(message.name))
            return;
        // Membership changes are low-frequency: record each, with the resulting other-connection count.
        recordTeamHubDiag('collab:recv', {
            sessionId: room.sessionId,
            name: message.name,
            outcome: 'membership',
        });
        if (room.buffer) {
            // A join is in flight: its member snapshot may predate this message.
            room.buffer.push(message);
            return;
        }
        const before = distinctUsers(room.members);
        this.applyMembership(room, message);
        if (message.name === ROOM_REVOKED) {
            this.endRoom(room);
            return;
        }
        if (message.name === ROOM_EXPIRED) {
            // Our membership's grant ran out: renew it quietly.
            this.renewSoon(room, 0);
            return;
        }
        this.emitParticipantChanges(room, before);
    }
    /** Joined adds a connection, left removes it; either is a no-op when already so. */
    applyMembership(room, message) {
        const member = message.payload;
        if (!member?.connectionId)
            return;
        if (message.name === MEMBER_JOINED && member.userId) {
            room.members.set(member.connectionId, member.userId);
        }
        else if (message.name === MEMBER_LEFT) {
            room.members.delete(member.connectionId);
        }
    }
    /** collabParticipantJoined/Left for each USER whose first connection appeared or last went. */
    emitParticipantChanges(room, before) {
        const after = distinctUsers(room.members);
        for (const userId of after) {
            if (!before.has(userId)) {
                this.emit('collabParticipantJoined', {
                    sessionId: room.sessionId,
                    userId,
                    displayName: null,
                });
            }
        }
        for (const userId of before) {
            if (!after.has(userId)) {
                this.emit('collabParticipantLeft', { sessionId: room.sessionId, userId, displayName: null });
            }
        }
    }
    infoOf(room) {
        const session = room.session;
        if (!session)
            return null;
        return {
            sessionId: room.sessionId,
            presenterId: session.presenterId,
            presenterName: session.presenterName,
            studyId: session.studyId,
            caseId: session.caseId,
            participantUserIds: [...distinctUsers(room.members)],
        };
    }
    /**
     * Joins the session's room. `started` is the presenter's own start response, whose grant is
     * used instead of fetching another. Resolves null when there is no way in (a read-only
     * member, an ended session, a revoked user) and also when not connected; rejects on a
     * transient failure (the caller retries). Already in the room on this connection: answers
     * from what is known, without a new grant.
     */
    async joinCollab(sessionId, started) {
        if (!this.connection || !this.isConnected())
            return null;
        const room = this.rooms.get(sessionId);
        if (room?.joining)
            return room.joining;
        if (room && room.epoch === this.epoch)
            return this.infoOf(room);
        return this.startJoin(sessionId, started ?? null);
    }
    startJoin(sessionId, started) {
        let room = this.rooms.get(sessionId);
        const isNew = !room;
        if (!room) {
            room = {
                sessionId,
                session: null,
                members: new Map(),
                canPublish: false,
                expiresAt: 0,
                epoch: -1,
                joining: null,
                buffer: null,
                refreshTimer: null,
            };
            this.rooms.set(sessionId, room);
        }
        const target = room;
        this.clearRefresh(target);
        target.buffer = [];
        const run = async () => {
            let offered = started;
            for (;;) {
                const epoch = this.epoch;
                try {
                    return await this.joinOnce(target, offered, epoch);
                }
                catch (err) {
                    offered = null;
                    // The connection changed under the join: join again on the new one.
                    if (this.epoch !== epoch && this.isConnected() && this.rooms.get(sessionId) === target) {
                        target.buffer = [];
                        continue;
                    }
                    throw err;
                }
            }
        };
        const promise = run().then((info) => {
            if (target.joining === promise)
                target.joining = null;
            if (info === null && this.rooms.get(sessionId) === target) {
                // No way in. A room this client was already in has ended for it.
                if (isNew)
                    this.dropRoom(sessionId);
                else
                    this.endRoom(target);
            }
            return info;
        }, (err) => {
            if (target.joining === promise)
                target.joining = null;
            if (this.rooms.get(sessionId) === target) {
                const buffered = target.buffer ?? [];
                target.buffer = null;
                if (buffered.some((m) => m.name === ROOM_REVOKED))
                    this.endRoom(target);
                else if (isNew)
                    this.dropRoom(sessionId);
                else {
                    // A failed renewal leaves the membership it already had: what arrived while the
                    // join was in flight changed that set, so apply it. After a reconnect (an older
                    // epoch) the membership is gone and the buffer means nothing.
                    if (target.epoch === this.epoch) {
                        const before = distinctUsers(target.members);
                        for (const message of buffered)
                            this.applyMembership(target, message);
                        this.emitParticipantChanges(target, before);
                    }
                    this.renewSoon(target, GRANT_RETRY_MS);
                }
            }
            throw err;
        });
        target.joining = promise;
        return promise;
    }
    /** One grant + joinRoom, with the contract's single retry on a spent grant. */
    async joinOnce(room, started, epoch) {
        const connection = this.connection;
        if (!connection || !this.isConnected())
            throw new Error('teamHub not connected');
        let session = started;
        for (let attempt = 0;; attempt += 1) {
            if (!session?.grant) {
                session = await fetchSession(room.sessionId);
                if (!session?.active || !session.grant)
                    return null;
            }
            try {
                const result = await connection.invoke('joinRoom', session.grant.token);
                return this.completeJoin(room, session, result, epoch);
            }
            catch (err) {
                const code = hubErrorCode(err);
                if (code === 'grant_revoked' || code === 'grant_user_mismatch')
                    return null;
                // `unavailable` burns the grant (a Redis fault); the presenter's start grant may also be
                // stale or spent by now. Either way: one more try with a fresh grant.
                const retryable = code === 'unavailable' || code === 'grant_used' || code === 'invalid_grant';
                if (attempt === 0 && (retryable || started !== null) && code !== null) {
                    session = null;
                    started = null;
                    continue;
                }
                throw err;
            }
        }
    }
    completeJoin(room, session, result, epoch) {
        if (this.rooms.get(room.sessionId) !== room) {
            // Left (or stopped) while the join was in flight: undo it on the server. Unless a newer
            // join for the same session has started since: the server holds one membership per
            // connection and room, so leaving here would undo that join too.
            if (!this.rooms.has(room.sessionId)) {
                void this.connection?.invoke('leaveRoom', result.roomId).catch(() => { });
            }
            return null;
        }
        if (this.epoch !== epoch)
            throw new Error('teamHub connection changed during join');
        const before = distinctUsers(room.members);
        const buffered = room.buffer ?? [];
        room.buffer = null;
        room.session = session;
        room.canPublish = result.canPublish;
        room.expiresAt = Date.parse(result.expiresAt);
        room.epoch = epoch;
        // The snapshot, then whatever was announced after it was read.
        room.members = new Map(result.members.map((m) => [m.connectionId, m.userId]));
        let revoked = false;
        for (const message of buffered) {
            if (message.name === ROOM_REVOKED)
                revoked = true;
            else
                this.applyMembership(room, message);
            // A buffered room.expired belonged to the membership this join just replaced.
        }
        if (revoked) {
            this.endRoom(room);
            return null;
        }
        this.emitParticipantChanges(room, before);
        this.scheduleRefresh(room);
        return this.infoOf(room);
    }
    /** Renew the grant before the membership ends (at ~80% of what is left). */
    scheduleRefresh(room) {
        const remaining = room.expiresAt - Date.now();
        const delay = Number.isFinite(remaining) ? remaining * GRANT_REFRESH_FRACTION : 0;
        this.renewSoon(room, Math.max(MIN_GRANT_REFRESH_MS, delay));
    }
    renewSoon(room, delayMs) {
        this.clearRefresh(room);
        room.refreshTimer = setTimeout(() => {
            room.refreshTimer = null;
            if (this.rooms.get(room.sessionId) !== room || room.joining || !this.isConnected())
                return;
            void this.startJoin(room.sessionId, null).catch((err) => {
                console.debug('teamHub grant renewal failed', err);
            });
        }, delayMs);
    }
    clearRefresh(room) {
        if (room.refreshTimer !== null)
            clearTimeout(room.refreshTimer);
        room.refreshTimer = null;
    }
    dropRoom(sessionId) {
        const room = this.rooms.get(sessionId);
        if (!room)
            return;
        this.clearRefresh(room);
        this.rooms.delete(sessionId);
    }
    /** The session is over for this client (revoked, or no grant on renewal). */
    endRoom(room) {
        if (this.rooms.get(room.sessionId) !== room)
            return;
        this.dropRoom(room.sessionId);
        this.emit('collabEnded', { sessionId: room.sessionId });
    }
    async leaveCollab(sessionId) {
        if (!this.rooms.has(sessionId))
            return;
        this.dropRoom(sessionId);
        const connection = this.connection;
        if (!connection || !this.isConnected())
            return;
        try {
            await connection.invoke('leaveRoom', sessionId);
        }
        catch (err) {
            console.debug('teamHub leaveRoom failed', err);
        }
    }
    async sendCollabState(sessionId, state) {
        await this.relay(sessionId, COLLAB_STATE, state);
    }
    async sendCollabPointer(sessionId, pointer) {
        await this.relay(sessionId, COLLAB_POINTER, pointer);
    }
    /** Presenter → room. Ephemeral: a refused or dropped relay is logged and never thrown. The
     *  relay outcome is recorded (edge-only, per name) so a broadcast that is silently dropped —
     *  no room, not connected, no publish right, or a stale epoch after a reconnect — is visible. */
    async relay(sessionId, name, payload) {
        const relayDiag = this.diagFor(this.relayDiags, name, Infinity);
        const room = this.rooms.get(sessionId);
        const connection = this.connection;
        if (!room) {
            relayDiag('no_room', 'collab:relay:result', { sessionId, name, outcome: 'no_room' });
            return;
        }
        if (!connection || !this.isConnected()) {
            relayDiag('not_connected', 'collab:relay:result', {
                sessionId,
                name,
                outcome: 'not_connected',
            });
            return;
        }
        if (!room.canPublish) {
            relayDiag('not_publishable', 'collab:relay:result', {
                sessionId,
                name,
                outcome: 'not_publishable',
            });
            return;
        }
        if (room.epoch !== this.epoch) {
            relayDiag('epoch_mismatch', 'collab:relay:result', {
                sessionId,
                name,
                outcome: 'epoch_mismatch',
                roomEpoch: room.epoch,
                epoch: this.epoch,
            });
            return;
        }
        try {
            await connection.invoke('sendToRoom', sessionId, name, payload);
            relayDiag('ok', 'collab:relay:result', { sessionId, name, outcome: 'ok' });
        }
        catch (err) {
            const code = hubErrorCode(err);
            relayDiag(`err:${code ?? 'unknown'}`, 'collab:relay:result', {
                sessionId,
                name,
                outcome: 'error',
                code,
            });
            console.debug(`teamHub sendToRoom ${name} failed`, err);
            // The membership ran out before the renewal landed: renew now.
            if (code === 'grant_expired' && !room.joining)
                this.renewSoon(room, 0);
        }
    }
    // --- HTTP-backed signals ---------------------------------------------------
    /** HTTP to the chat service (POST /chat/status), not a socket call: it works whether or not
     * the connection is up. Kept here so its callers need not know which.
     *
     * Coalesced around the service's one-change-a-second throttle: a 429 for the newest choice
     * queues ONE retry after the window, carrying whatever is newest when it fires (Away then Busy
     * inside a second ends on Busy, not on Away). Choices made while that retry waits ride on it.
     * Never rejects: the outcome says whether the caller's optimistic status stands. */
    async setStatus(status) {
        this.wantedStatus = status;
        if (this.statusRetry)
            return this.outcomeFor(status, this.statusRetry);
        try {
            await setTeamStatus(status);
            return this.wantedStatus === status ? 'persisted' : 'superseded';
        }
        catch (err) {
            if (this.wantedStatus !== status)
                return 'superseded';
            if (err instanceof ApiError && err.status === 429) {
                return this.outcomeFor(status, this.retryStatusSoon());
            }
            console.warn('teamHub setStatus failed', err);
            return 'failed';
        }
    }
    /** A caller's own outcome from the shared retry: only the status it actually sent counts. */
    async outcomeFor(status, retry) {
        const { sent, outcome } = await retry;
        return sent === status ? outcome : 'superseded';
    }
    retryStatusSoon() {
        this.statusRetry ??= new Promise((resolve) => {
            setTimeout(() => {
                // Choices made from here on go out on their own (and retry on their own).
                this.statusRetry = null;
                const sent = this.wantedStatus;
                if (sent === null) {
                    resolve({ sent, outcome: 'superseded' });
                    return;
                }
                setTeamStatus(sent).then(() => resolve({ sent, outcome: this.wantedStatus === sent ? 'persisted' : 'superseded' }), (err) => {
                    if (this.wantedStatus !== sent) {
                        resolve({ sent, outcome: 'superseded' });
                        return;
                    }
                    console.warn('teamHub setStatus retry failed', err);
                    resolve({ sent, outcome: 'failed' });
                });
            }, STATUS_RETRY_MS);
        });
        return this.statusRetry;
    }
    /**
     * Actively confirms the connection is really alive by invoking the server's trivial `ping` with
     * a deadline, instead of trusting the socket's own `state`. A connection a backgrounded tab still
     * reports as `connected` but the service already reaped (its keep-alive pings stopped while the
     * tab was frozen) never answers — so the DEADLINE, not the socket state, is what detects it. The
     * provider calls this on tab refocus and on a periodic timer, and reconnects on `dead`.
     */
    async verifyLiveness(timeoutMs = LIVENESS_TIMEOUT_MS) {
        const connection = this.connection;
        if (!connection || !this.isConnected()) {
            recordTeamHubDiag('liveness:skip', { reason: 'not_connected', state: this.state });
            return 'skipped';
        }
        if (this.verifying) {
            recordTeamHubDiag('liveness:skip', { reason: 'in_flight' });
            return 'skipped';
        }
        this.verifying = true;
        let timer;
        try {
            const deadline = new Promise((resolve) => {
                timer = setTimeout(() => resolve(false), timeoutMs);
            });
            const probe = connection
                .invoke('ping')
                .then(() => true)
                .catch(() => false);
            const alive = await Promise.race([probe, deadline]);
            // The socket may have been replaced while we waited (a reconnect, a stop): only speak for the
            // connection we actually probed.
            if (this.connection !== connection) {
                recordTeamHubDiag('liveness:stale', { alive });
                return 'skipped';
            }
            recordTeamHubDiag('liveness:result', { alive, timeoutMs, state: this.state });
            return alive ? 'alive' : 'dead';
        }
        finally {
            if (timer !== undefined)
                clearTimeout(timer);
            this.verifying = false;
        }
    }
    /**
     * Tears the connection down and starts a fresh one at once (a new ticket, re-joined rooms). For a
     * confirmed-dead socket that still reports `connected`: teardown() flips the state to `idle`,
     * which is what breaks the provider's retry-skip trap ("state is connected, nothing to do"), and
     * the immediate start reconnects now rather than after the idle-restart backoff.
     */
    async forceReconnect(reason) {
        recordTeamHubDiag('forceReconnect', { reason, state: this.state });
        await this.teardown();
        await this.ensureStarted();
    }
    /** HTTP to the chat service (POST /chat/conversations/{id}/typing). Ephemeral: a failure is
     * dropped, like the socket traffic it replaced. */
    async sendTyping(conversationId) {
        try {
            await sendTeamTyping(conversationId);
        }
        catch (err) {
            console.warn('teamHub sendTyping failed', err);
        }
    }
}
export const teamHub = new TeamHubService();
